// src/lib/ThemeContext.jsx
// Centralized dark mode state and display-safe color helpers.
// Any component can call useDisplayColors(pc, sc) to get colors
// that are readable in both light and dark mode — no prop drilling needed.

import React, { createContext, useContext, useState, useEffect } from 'react';
import { enableDarkOverrides, disableDarkOverrides } from './darkModeOverrides';

const LFG_COLORS = {
  navy: '#1A3665',
  gray: '#707578',
  green: '#85BD66',
};

// Dark-mode equivalents — light enough to read on slate-800/900 backgrounds
const DARK = {
  primary:   '#7dd3fc', // sky-300
  secondary: '#94a3b8', // slate-400
  navy:      '#7dd3fc', // sky-300
  green:     '#6ee7b7', // emerald-300
};

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem('lfg_darkMode') === 'true'; }
    catch { return false; }
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    try { localStorage.setItem('lfg_darkMode', String(darkMode)); } catch {}
    if (darkMode) enableDarkOverrides();
    else disableDarkOverrides();
    return () => { if (darkMode) disableDarkOverrides(); };
  }, [darkMode]);

  return (
    <ThemeContext.Provider value={{ darkMode, setDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}

/**
 * Returns display-safe color variables for a component.
 * Usage: const { displayPrimary, displaySecondary, displayNavy, displayGreen } = useDisplayColors(pc, sc);
 */
export function useDisplayColors(pc, sc) {
  const { darkMode } = useTheme();
  const primary   = pc || LFG_COLORS.navy;
  const secondary = sc || LFG_COLORS.gray;
  return {
    darkMode,
    displayPrimary:   darkMode ? DARK.primary   : primary,
    displaySecondary: darkMode ? DARK.secondary : secondary,
    displayNavy:      darkMode ? DARK.navy      : LFG_COLORS.navy,
    displayGreen:     darkMode ? DARK.green     : LFG_COLORS.green,
  };
}
